import { GetSearchRequestsModel, ISearchHistoryService, SearchRequestModel } from '@truckdown/locations';
import { Constants, ServiceModel } from '@truckdown/systems';
import context from '@truckdown/components-systems';
import { defineComponent, ref, Ref, computed, onMounted } from 'vue';

const item_per_page = 15;

interface ILastKey {
    lastId?: string,
    lastTime?: Date
}

interface IItem {
    location: string,
    details: string,
    service: string,
    model: SearchRequestModel
}

interface IData {
    list: Ref<IItem[]>,
    total: Ref<number>,
    type: Ref<string>,
    loading: Ref<boolean>,
    pages: Ref<ILastKey[]>,
    current: Ref<ILastKey>
}

const getData = function () {
    return {
        list: ref<IItem[]>([]),
        total: ref<number>(0),
        type: ref<string>('user'),
        loading: ref<boolean>(false),
        pages: ref<ILastKey[]>([]),
        current: ref<ILastKey>({})
    } as IData;
};

const getComputed = function (data: IData) {
    return {
        pagerText: computed(() => {
            let previous = (data.pages.value.length * item_per_page);
            let start = previous + 1;
            let end = previous + data.list.value.length;
            return 'Displaying ' + start + ' to ' + end + ' of ' + data.total.value;
        }),
        enablePrevious: computed(() => {
            return !data.loading.value && data.pages.value.length > 0;
        }),
        enableNext: computed(() => {
            let previous = (data.pages.value.length * item_per_page);
            return !data.loading.value && (previous + data.list.value.length) < data.total.value;
        })
    };
}

const getLocation = function (search: SearchRequestModel) {
    let cityDisplay = search.meta['city'] + ', ' + search.meta['regionCode'];

    var cityId = search.meta['cityId'];
    if (cityId && (cityId as string).length > 0) {
        return cityDisplay;
    }

    return (search.meta['locationDisplay'] as string | undefined) ?? cityDisplay;
}

const getDetails = function (search: SearchRequestModel) {
    return 'Search ran by ' + search.userDisplay + ' on ' + new Date(search.time).toLocaleDateString("en-US", {
        weekday: 'short',
        month: 'short',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
}

const getServices = function (search: SearchRequestModel, services: ServiceModel[]) {
    let selected = services.filter((svs) => {
        return search.services.some((cd) => {
            return cd == svs.code;
        });
    });

    return selected.map((svs) => {
        return svs.name;
    }).join(', ');
}

const getItem = function (model: SearchRequestModel, services: ServiceModel[]): IItem {
    return {
        location: getLocation(model),
        details: getDetails(model),
        service: getServices(model, services),
        model: model
    }
}

const runSearch = function (data: IData, services: ServiceModel[], lastKey?: ILastKey) {
    data.loading.value = true;
    context.getService<ISearchHistoryService>('ISearchHistoryService')
        .then((service) => {
            return service.getRequestHistory({
                showAll: data.type.value == 'account',
                limit: item_per_page,
                lastId: lastKey?.lastId,
                lastTime: lastKey?.lastTime
            } as GetSearchRequestsModel)
                .then((res) => {
                    if (lastKey) {
                        if (lastKey.lastId == data.list.value[data.list.value.length - 1].model.id) {
                            //next
                            data.pages.value.push(data.current.value);
                        } else if (lastKey.lastId != data.current.value.lastId) {
                            //previous
                            data.pages.value.pop();
                        }
                        data.current.value = lastKey;
                    } else {
                        data.pages.value = [];
                        data.current.value = {};
                    }
                    data.total.value = res.count;
                    data.list.value = res.results
                        .map((m) => { return getItem(m, services); });
                });
        })
        .catch((reason) => {
            console.log('Error getting search history', reason);
            data.total.value = 0;
            data.list.value = [];
        })
        .finally(() => {
            data.loading.value = false;
        });
}

const getServiceUrlPortion = function (request: SearchRequestModel, services: ServiceModel[]) {
    if (request.services.length > 1) {
        return '/multiple-services';
    } else if (request.services.length == 1) {
        let service = services.find((svs) => {
            return svs.code == request.services[0];
        });
        if (service) {
            return '/' + encodeURIComponent(service.name.toLowerCase().replace(/\s/g, '-'));
        }
    }
    return '/unknown';
}

const getLocationUrlPortion = function (request: SearchRequestModel) {
    let location = request.meta['locationDisplay'] as string ?? request.meta['city'] as string;
    if (location) {
        return '/' + encodeURIComponent(location);
    }
    return '/unknown';
}

const getCityPortion = function (request: SearchRequestModel) {
    let location = request.meta['cityId'] as string;
    if (location) {
        return '/' + encodeURIComponent(location);
    }
    return '';
}

const getServicesQuery = function (request: SearchRequestModel) {
    return '?svs=' + request.services.join('-');
}

const getAmentityQuery = function (request: SearchRequestModel) {
    if (request.filters.length > 0) {
        return '&ams=' + request.filters.join('-');
    }
    return '';
}

const getOptionsQuery = function(request: SearchRequestModel) {
    return (request.options.searchByDistance ? '&sod=true' : '')
        + (request.options.suppressManagedServices ? '&sms=true' : '');
}

const getLocationQuery = function (request: SearchRequestModel) {
    let location = request.meta['cityId'] as string;
    //no options if has a cityId
    if (location) {
        return '';
    }

    return '&lat=' + request.meta['lat']
        + '&lng=' + request.meta['lng']
        + '&ctryCd=' + request.meta['countryCode']
        + '&rgnCd=' + request.meta['regionCode'];
}

const generateUrl = function (request: SearchRequestModel, services: ServiceModel[]) {
    return '/search/results'
        + getServiceUrlPortion(request, services)
        + getLocationUrlPortion(request)
        + getCityPortion(request)
        + getServicesQuery(request)
        + getLocationQuery(request)
        + getAmentityQuery(request)
        + getOptionsQuery(request);
}

const getMethods = function (data: IData, services: ServiceModel[]) {
    return {
        onPrevious: function (ev: Event) {
            if (!data.loading.value) {
                data.loading.value = true;

                let lastKey = data.pages.value[data.pages.value.length - 1];
                runSearch(data, services, lastKey);
            }
        },
        onNext: function (ev: Event) {
            if (!data.loading.value) {
                data.loading.value = true;

                let lastListing = data.list.value[data.list.value.length - 1];
                let lastKey = {
                    lastTime: new Date(lastListing.model.time),
                    lastId: lastListing.model.id
                } as ILastKey;
                runSearch(data, services, lastKey);
            }
        },
        changeType: function (ev: Event) {
            runSearch(data, services);
        },
        runSearch: function (model: SearchRequestModel) {
            data.loading.value = true;
            let url = generateUrl(model, services);
            window.location.href = url;
        }
    };
}

export default defineComponent({
    props: {
        services: {
            type: Array as () => ServiceModel[],
            required: true
        }
    },
    emits: [],
    setup: function (props, ctx) {
        let data = getData();
        let computed = getComputed(data);
        let methods = getMethods(data, props.services);

        onMounted(() => {
            runSearch(data, props.services);
        });

        return {
            ...data,
            ...computed,
            ...methods
        };
    }
});
